import * as ko from "knockout";
import { Modal, Tab as BootstrapTab } from "bootstrap";
import { AdminEvent } from "static/classes/Event";
import { AdminPerson } from "static/classes/Person";
import { AdminArtist } from "static/classes/Artist";
import { AdminHost } from "static/classes/Host";
import { ARTIST_STATUSES, personLocale } from "static/common/constants";
import { QuickMessageTemplateVM } from "static/components/email-composer";

declare const scvm: any;

// Define the tab structure
type Tab = {
  id: string;
  component: string;
  label: string;
  defaultIcon?: string;
  icon?: ko.Observable<string>;
  count?: ko.Observable<number>;
  active: ko.Observable<boolean>;
  hidden: ko.Observable<boolean>;
  disabled: ko.Observable<boolean>;
};

// Optionally type the params your component will receive
type DetailsModalParams = {
  id?: string;
  hidden?: Record<string, boolean>;
};

type EventData = {
  message_count?: ko.Observable<number>;
  [key: string]: any;
};

type ProvidedData = {
  type: string;
  [key: string]: any;
};

const tabs = [
      {
        id: 'host',
        component: 'tab-host',
        label: 'Host',
        active: ko.observable(true),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'artist',
        component: 'tab-artist',
        label: 'Artist',
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'events',
        component: 'tab-events',
        label: 'Event list',
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'history',
        component: 'tab-history',
        label: 'Event History',
        defaultIcon: 'mdi-history',
        icon: ko.observable('mdi-history'),
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'chat',
        component: 'tab-chat',
        label: 'Chat',
        defaultIcon: 'mdi-chat',
        icon: ko.observable('mdi-chat'),
        count: ko.observable(0),
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'call',
        component: 'tab-call',
        label: 'Call',
        defaultIcon: 'mdi-phone',
        icon: ko.observable('mdi-phone'),
        count: ko.observable(0),
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      },
      {
        id: 'mail',
        component: 'tab-mail',
        label: 'Mail',
        defaultIcon: 'mdi-email',
        icon: ko.observable('mdi-email'),
        count: ko.observable(0),
        active: ko.observable(false),
        hidden: ko.observable(false),
        disabled: ko.observable(false)
      }

] as const satisfies Tab[];

type TabId = typeof tabs[number]["id"]

export class DetailsModalViewModel {
  type = "DetailsModalViewModel";
  id: string;
  allTabs = ko.observableArray(tabs);
  tabs: ko.Computed<Tab[]>;
  // data.host is the AdminPerson behind the host tab. The person carries the
  // host record itself via its nested .host() (an AdminHost) when one exists,
  // so templates read person fields directly off data.host() and host fields
  // off data.host().host(). The slot is named `host` because that's where
  // the user lives in this tab even when they have no formal host profile.
  data = {
    artist: ko.observable<AdminArtist | null>(null),
    host: ko.observable<AdminPerson | null>(null),
    event: ko.observable<AdminEvent | null>(null),
    events: ko.observableArray<AdminEvent>([])
  };

  openStates = ['waiting_for_approval', 'waiting_for_confirmation'];
  hasOpenEvents: ko.Computed<boolean>;

  // Exposed so the artist tab's state dropdown can bind to it.
  artist_statuses = ARTIST_STATUSES;

  // Quick-message composer, shown stacked on top of the details modal (BS5
  // layers it over the open modal). The matching #detailsModalQuickMessage
  // <email-composer> lives in details-modal.html so it travels with the
  // (global) details modal regardless of which page opened it.
  quick_message_vm = new QuickMessageTemplateVM({ rootvm: scvm });

  set_send_quick_message = (person: AdminPerson) => {
    const vm = this.quick_message_vm;
    vm.display_name(scvm.webapp_session().user().display_name());
    vm.person_uuid(person.person_uuid?.() ?? '');
    vm.recipient_name(person.display_name?.() ?? '');
    // Reset first so language.subscribe re-populates even when unchanged.
    vm.language(undefined as any);
    vm.language(personLocale(person));
    Modal.getOrCreateInstance(document.getElementById('detailsModalQuickMessage')!).show();
  };

  // Message the admin wants to mark as "not a bypass", confirmed in the
  // native <dialog> in tabs/chat.html.
  notBypassMessage = ko.observable<any>(null);
  notBypassError = ko.observable<string | null>(null);

  // Keeps the chat tab alert icon in sync when a message is marked not a bypass.
  private chatIcon?: ko.Computed<void>;

  askNotBypass = (message: any) => {
    this.notBypassMessage(message);
    this.notBypassError(null);
    (document.getElementById('details-modal-not-bypass-dialog') as HTMLDialogElement).showModal();
  };

  confirmNotBypass = async () => {
    const dialog = document.getElementById('details-modal-not-bypass-dialog') as HTMLDialogElement;
    try {
      await this.notBypassMessage().report_no_filter();
      dialog.close();
      this.notBypassMessage(null);
    } catch (e: any) {
      this.notBypassMessage().reported(false);
      this.notBypassError(e?.message ?? 'Could not mark the message');
    }
  };

  constructor(params: DetailsModalParams) {
    this.id = params.id ?? '';
    this.tabs = ko.computed<Tab[]>(() => this.allTabs().filter(tab => tab.hidden() === false));
    this.hasOpenEvents = ko.computed(() =>
      this.data.events().some(e => this.openStates.includes(e.current_state()?.event_state()))
    );
  }

  goToTab(tabId: TabId) {
    // console.log(`Navigating to tab: ${tabId}`);
    const triggerEl = document.querySelector(`#details-modal-nav button[data-bs-target="#details-modal-tab-${tabId}"]`);
    if (triggerEl) {
      const tabInstance = BootstrapTab.getOrCreateInstance(triggerEl);
      tabInstance.show(); // Select tab by name
    } else {
      console.error(`Tab with id ${tabId} not found.`);
    }
  }

  async setData(data: ProvidedData, requestedTabId?: string) {
    if (!data.hasOwnProperty('type')){ throw new Error("Provide data has no type defined.");}

    // Reset data
    this.data.artist(null);
    this.data.host(null);
    this.data.event(null);
    this.data.events([]);

    this.chatIcon?.dispose();
    this.chatIcon = undefined;

    // Reset tabs (iterate allTabs, not the filtered `tabs`, so hidden ones get reset too)
    for (const tab of this.allTabs() as readonly Tab[]) {
      if (ko.isObservable(tab.icon) && tab.defaultIcon) {
        tab.icon(tab.defaultIcon)
      }
      if (ko.isObservable(tab.count)) {
        tab.count(0)
      }
      tab.active(false);
      tab.hidden(false);
      tab.disabled(false);
    };

    if (['AdminPerson', 'BasePerson', 'Person'].includes(data.type)) {
      const person = await (new AdminPerson({ person_uuid: data.person_uuid() })).lookupDetails();

      // /api/admin/person doesn't include artist data, so fall back to the row's
      // artist (it was already populated by /api/admin/people for the listing).
      const artist: AdminArtist | undefined =
        person.artist().artist_uuid() ? person.artist() :
        data.artist?.()?.artist_uuid?.() ? data.artist() :
        undefined;
      if (artist) {
        // The artist's nested .person was stripped during construction to break
        // the cycle; plug the full person back in so the artist tab renders.
        artist.person(person);
        this.data.artist(artist);
      } else {
        const artistTab = this.allTabs().find(tab => tab.id === 'artist');
        if (artistTab) artistTab.disabled(true);
      }

      // Host tab: show only if person has a host record, or as fallback when
      // there's no artist either (so the modal isn't entirely empty).
      if (person.host().host_uuid() || !artist) {
        this.data.host(person);
      } else {
        const hostTab = this.allTabs().find(tab => tab.id === 'host');
        if (hostTab) hostTab.disabled(true);
      }

      if (data.events().length > 0) {
        for (const event of data.events()) {
          await event.lookupDetails();
        }
        this.data.events(data.events());
      }

      const mailTab = this.allTabs().find(tab => tab.id === 'mail');
      if (mailTab && ko.isObservableArray(person.email_sent_log)) {
        mailTab.count(person.email_sent_log().length);
      }
    } else if (['Artist', 'AdminArtist'].includes(data.type)) {
      let artist: AdminArtist;
      if (data.type === 'AdminArtist') {
        artist = data as AdminArtist;
        this.data.artist(artist);
      } else {
        artist = new AdminArtist({
          artist_uuid: data.artist_uuid(),
          person_uuid: data.person_uuid(),
        });
        this.data.artist(artist);
      }
      // Only populate the host tab if the artist's person has a host profile;
      // person details on their own are already shown on the artist tab.
      const personUuid = artist.person().person_uuid();
      let hasHostProfile = false;
      if (personUuid) {
        const person = await (new AdminPerson({ person_uuid: personUuid })).lookupDetails();
        if (person.host().host_uuid()) {
          this.data.host(person);
          hasHostProfile = true;
        }
      }
      if (!hasHostProfile) {
        const hostTab = this.allTabs().find(tab => tab.id === 'host');
        if (hostTab) hostTab.disabled(true);
      }
    } else if (['Host', 'AdminHost'].includes(data.type)) {
      // Look up the host's person fresh: the API attaches the host record to
      // the response, so the resulting AdminPerson has nested .host() filled in.
      const host = data as AdminHost;
      const personUuid = host.person_uuid() || host.person()?.person_uuid?.();
      if (personUuid) {
        const person = await (new AdminPerson({ person_uuid: personUuid })).lookupDetails();
        this.data.host(person);
        if (person.artist().artist_uuid()) {
          this.data.artist(person.artist());
        }
      }
    } else if (data.type === 'AdminEvent') {
      const eventData = data as AdminEvent;
      await eventData.lookupDetails();
      this.data.event(eventData);

      const chatTab = this.allTabs().find(tab => tab.id === 'chat');
      if (chatTab && ko.isObservableArray(eventData.messages)) {
        chatTab.count(eventData.message_count());
        this.chatIcon = ko.computed(() => {
          const hasBypass = eventData.messages().some((m: any) => m.filtered_message());
          chatTab.icon!(hasBypass ? 'mdi-alert text-danger' : chatTab.defaultIcon!);
        });
      }

      const mailTab = this.allTabs().find(tab => tab.id === 'mail');
      if (mailTab) {
        mailTab.count(eventData.emails()?.length);
      }

      const callTab = this.allTabs().find(tab => tab.id === 'call');
      if (callTab && ko.isObservableArray(eventData.calls)) {
        callTab.count(eventData.calls().length);
        if (eventData.calls().some((c: any) => c.bypass_attempts)) {
          callTab.icon('mdi-alert text-danger');
        }
      }

      // Host tab person: for a host event, look up the host's person (so we
      // get the host record nested under it); for creator events, use the creator.
      if (data.host_uuid()) {
        const personUuid = eventData.host().person_uuid() || eventData.host().person()?.person_uuid?.();
        if (personUuid) {
          const person = await (new AdminPerson({ person_uuid: personUuid })).lookupDetails();
          this.data.host(person);
        } else {
          this.data.host(eventData.creator());
        }
      } else {
        this.data.host(eventData.creator());
      }
      const eventArtist = eventData.artist();
      this.data.artist(eventArtist);
      // lookupDetails() strips person from the artist to break circular refs, so
      // the artist tab has no phone/email unless we look the person up explicitly.
      const artistPersonUuid = eventArtist?.person_uuid?.();
      if (artistPersonUuid && !eventArtist?.person?.()?.person_uuid?.()) {
        const artistPerson = await (new AdminPerson({ person_uuid: artistPersonUuid })).lookupDetails();
        eventArtist.person(artistPerson);
      }
    } else if (data.type === 'SCCombinedEvent') {
      const creator = await (new AdminPerson({ person_uuid: data.creator_uuid() })).lookupDetails();
      this.data.host(creator);

      const events = data.invites().map((e: AdminEvent | object) => {
        if (e instanceof AdminEvent) return e;
        return new AdminEvent(e);
      });
      await Promise.all(events.map((e: AdminEvent) => e.lookupDetails()));
      this.data.events(events);

      // Disable tabs that don't have data in this view
      const relatedTabIds: TabId[] = ['chat', 'call', 'mail', 'history', 'artist']
      for (const tab of this.tabs()) {
        if (relatedTabIds.includes(tab.id as TabId)) {
          tab.disabled(true);
        }
      }
    } else {
      console.debug(data);
      throw new Error(`Unknown type ${data.type} provided to DetailsModalViewModel`);
    }

    // Whatever source populated data.artist above (people listing row,
    // event-/host-nested artist, ...) may have arrived without its
    // current_state action, which leaves the artist-state widget blank. Reload
    // it in the background (fire-and-forget) so the modal renders immediately;
    // data.artist is observable, so the artist tab updates when it resolves.
    void this.ensureArtistState(this.data.artist());

    // Notify Knockout about the change
    this.allTabs.valueHasMutated();

    // Select the requested tab, falling back to the first enabled one if the
    // requested tab is disabled or hidden for this entity.
    if (requestedTabId) {
      const target = this.allTabs().find(t => t.id === requestedTabId);
      const usable = target && !target.hidden() && !target.disabled();
      const finalTabId = usable
        ? requestedTabId
        : this.allTabs().find(t => !t.hidden() && !t.disabled())?.id;
      if (finalTabId) this.goToTab(finalTabId as TabId);
    }
  };

  // Listing rows and event-/person-nested artists can arrive without their
  // current_state action or full state history (only /api/admin/artist returns
  // them), which leaves the artist tab's state widget blank ("Invalid date",
  // empty dropdown) and its State History timeline empty. When either is
  // missing, reload the full admin record.
  async ensureArtistState(artist: AdminArtist | null) {
    // Fully guarded: this runs un-awaited (fire-and-forget) so it must never
    // reject — an unhandled rejection here used to leave the modal empty.
    try {
      if (!artist?.artist_uuid?.()) return;
      const hasState = !!artist.current_state?.()?.artist_state?.();
      const hasHistory = (artist.artist_state_history?.() ?? []).length > 0;
      if (hasState && hasHistory) return;
      this.data.artist(await artist.lookupDetails());
    } catch (err) {
      console.error('Failed to look up artist current_state', err);
    }
  }

  async closeAllOpenEvents() {
    const person = this.data.host();
    if (!person) return;
    if (!window.confirm(`Close all open events for this user?`)) return;
    try {
      const response = await fetch('/api/admin/people/close-all-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: ko.toJSON({ person_uuid: person.person_uuid() }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message);
      alert(`Closed ${data.closed_count} event(s).`);
    } catch (err: any) {
      console.error(err);
      alert(`Error closing events: ${err.message}`);
    }
  }

  async setEventFromEvents(event: AdminEvent) {
    // console.log("Setting event from events tab", event);
    await event.lookupDetails();
    this.data.event(event);
    const eventArtist = event.artist();
    if (eventArtist?.type === 'AdminArtist') {
      this.data.artist(eventArtist as AdminArtist);
    } else if (eventArtist?.artist_uuid()) {
      const artist = new AdminArtist({
        artist_uuid: eventArtist.artist_uuid()
      });
      this.data.artist(artist);
    }
    void this.ensureArtistState(this.data.artist());
    // Enable some tabs, which might be disabled in SCCombinedEvent view
    for (const tab of this.tabs()) {
      if (['chat', 'call', 'mail', 'history', 'artist'].includes(tab.id)) {
        tab.disabled(false);
      }
    }
    this.goToTab('history');
  }
}

export const setDataAndShowDetailsModal = (data: ProvidedData, tabId?: string) => {
  const detailsModalDomElement = document.getElementById('detailsModal');

  if (!detailsModalDomElement) throw new Error("Missing global DetailsModal in DOM");

  // get ko context and set data; setData handles tab selection (with fallback
  // if the requested tab ends up disabled for this entity)
  const componentVm: DetailsModalViewModel = ko.contextFor(detailsModalDomElement).$component
  // setData is async and intentionally not awaited (the modal opens immediately);
  // catch so a rejection surfaces instead of silently leaving the modal blank.
  componentVm.setData(data, tabId).catch((err) => console.error('details-modal setData failed', err));

  const detailsModal = Modal.getOrCreateInstance(detailsModalDomElement);
  detailsModal?.show();
}
